import {
  Table,
  Popconfirm,
  Space,
  Input,
  Button,
  Grid,
  Typography,
  Tooltip,
  message,
} from "antd";
import { useEffect, useMemo, useState, useCallback } from "react";
import { useAppDispatch, useAppSelector } from "../../../../app/hooks";
import type { ColumnsType } from "antd/es/table";
import {
  IPppLinkObject,
  PppLinkOriginType,
  PppRedirectPage,
} from "../../../../interfaces/pppLink.interface";
import { formatLocalDate } from "../../../../utils/formatDate";
import styles from "./PppLinksTable.module.css";
import debounce from "lodash/debounce";
import {
  deletePppLinkThunk,
  listPppLinksThunk,
  selectPppLinks,
  selectPppLinksLastUpdatedKey,
  selectPppLinksLoading,
} from "../../pppLinksSlice";
import { CopyOutlined, ExclamationCircleOutlined } from "@ant-design/icons";

export default function PppLinksTable() {
  const dispatch = useAppDispatch();
  const screens = Grid.useBreakpoint();
  const isMobile = !screens.md;
  const links = useAppSelector(selectPppLinks);
  const loading = useAppSelector(selectPppLinksLoading);
  const lastUpdatedKey = useAppSelector(selectPppLinksLastUpdatedKey);
  const [search, setSearch] = useState<string>("");
  const debouncedSearchChange = useCallback(
    debounce(
      (v: string) => setSearch(v.toLowerCase().replace(/\s+/g, " ").trim()),
      400
    ),
    []
  );

  useEffect(() => {
    dispatch(listPppLinksThunk({ search: search || undefined }));
  }, [dispatch, lastUpdatedKey, search]);

  const copyToClipboard = useCallback(async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      message.success("URL copied");
    } catch {
      // Fallback for older browsers
      const textarea = document.createElement("textarea");
      textarea.value = text;
      textarea.style.position = "fixed";
      textarea.style.opacity = "0";
      document.body.appendChild(textarea);
      textarea.focus();
      textarea.select();
      try {
        document.execCommand("copy");
        message.success("URL copied");
      } catch {
        message.error("Failed to copy URL");
      } finally {
        document.body.removeChild(textarea);
      }
    }
  }, []);

  const columns: ColumnsType<IPppLinkObject> = useMemo(
    () => [
      {
        title: "Link",
        dataIndex: "fullUrl",
        key: "fullUrl_copy",
        width: 64,
        align: "center",
        render: (_: unknown, r) => (
          <Tooltip title="Copy full URL">
            <Button
              type="text"
              icon={<CopyOutlined />}
              onClick={() => copyToClipboard(r.fullUrl)}
              aria-label="Copy full URL"
            />
          </Tooltip>
        ),
        responsive: ["xs", "sm", "md", "lg", "xl", "xxl"],
        // fixed: isMobile ? "left" : undefined,
      },
      {
        title: "Code",
        dataIndex: "code",
        key: "code",
        width: 120,
        ellipsis: true,
        responsive: ["xs", "sm", "md", "lg", "xl", "xxl"],
      },
      {
        title: "Business Name",
        key: "businessName",
        width: 200,
        render: (_, r) =>
          typeof r.businessId === "object"
            ? (r.businessId as any).businessName
            : "-",
        ellipsis: true,
        responsive: ["xs", "sm", "md", "lg", "xl", "xxl"],
      },
      //   {
      //     title: "Business Id",
      //     key: "businessId",
      //     width: 200,
      //     render: (_, r) =>
      //       typeof r.businessId === "object"
      //         ? (r.businessId as any)._id
      //         : r.businessId,
      //     ellipsis: true,
      //     responsive: ["md", "lg", "xl", "xxl"],
      //   },
      {
        title: "Redirect Page",
        dataIndex: "redirectPage",
        key: "redirectPage",
        width: 160,
        responsive: ["sm", "md", "lg", "xl", "xxl"],
      },
      {
        title: "Origin Type",
        dataIndex: "originType",
        key: "originType",
        width: 140,
        responsive: ["md", "lg", "xl", "xxl"],
      },
      //   {
      //     title: "Expires At",
      //     dataIndex: "expiresAt",
      //     key: "expiresAt",
      //     render: (v) => (v ? formatLocalDate(new Date(v), true) : "-"),
      //     width: 180,
      //     responsive: ["lg", "xl", "xxl"],
      //   },
      {
        title: "Created At",
        dataIndex: "createdAt",
        key: "createdAt",
        render: (v) => (v ? formatLocalDate(new Date(v), true) : "-"),
        width: 180,
        responsive: ["xl", "xxl"],
      },
      //   {
      //     title: "Updated At",
      //     dataIndex: "updatedAt",
      //     key: "updatedAt",
      //     render: (v) => (v ? formatLocalDate(new Date(v), true) : "-"),
      //     width: 180,
      //     responsive: ["xl", "xxl"],
      //   },
      {
        title: "Actions",
        key: "actions",
        // fixed: isMobile ? "right" : undefined,
        width: 100,
        render: (_, r) => (
          <Space>
            <Popconfirm
              title="Delete PPP link?"
              description="This action cannot be undone."
              okText="Delete"
              okButtonProps={{ danger: true }}
              cancelText="Cancel"
              placement={isMobile ? "topRight" : "left"}
              icon={<ExclamationCircleOutlined style={{ color: "#ff4d4f" }} />}
              onConfirm={() => dispatch(deletePppLinkThunk({ code: r.code }))}
            >
              <Button type="link" danger>
                Delete
              </Button>
            </Popconfirm>
          </Space>
        ),
        responsive: ["xs", "sm", "md", "lg", "xl", "xxl"],
      },
    ],
    [dispatch, isMobile, copyToClipboard]
  );

  return (
    <div>
      <div className={styles.table_header}>
        <Input.Search
          placeholder="Search by code, business, url..."
          allowClear
          onChange={(e) => debouncedSearchChange(e.target.value)}
          style={{ width: "100%" }}
        />
      </div>

      <Table<IPppLinkObject>
        rowKey={(r) => r._id || r.code}
        dataSource={links}
        loading={loading}
        columns={columns}
        size={isMobile ? "small" : "middle"}
        scroll={{
          x: "max-content",
        }}
        pagination={{
          hideOnSinglePage: true,
          showSizeChanger: !isMobile,
        }}
        expandable={{
          expandRowByClick: false,
          rowExpandable: () => true,
          expandedRowRender: (r) => (
            <div style={{ paddingRight: 8 }}>
              <Space
                direction="vertical"
                size="small"
                style={{ width: "100%" }}
              >
                <div>
                  <Typography.Text type="secondary">Full URL: </Typography.Text>
                  <Typography.Text copyable={{ text: r.fullUrl }}>
                    {r.fullUrl}
                  </Typography.Text>
                </div>
                <div>
                  <Typography.Text type="secondary">Code: </Typography.Text>
                  <Typography.Text>{r.code}</Typography.Text>
                </div>
                <div>
                  <Typography.Text type="secondary">
                    Business Name:{" "}
                  </Typography.Text>
                  <Typography.Text>
                    {typeof r.businessId === "object"
                      ? (r.businessId as any).businessName
                      : "-"}
                  </Typography.Text>
                </div>
                <div>
                  <Typography.Text type="secondary">
                    Business Id:{" "}
                  </Typography.Text>
                  <Typography.Text>
                    {typeof r.businessId === "object"
                      ? (r.businessId as any)._id
                      : r.businessId}
                  </Typography.Text>
                </div>
                <div>
                  <Typography.Text type="secondary">
                    Redirect Page:{" "}
                  </Typography.Text>
                  <Typography.Text>{r.redirectPage}</Typography.Text>
                </div>
                <div>
                  <Typography.Text type="secondary">
                    Origin Type:{" "}
                  </Typography.Text>
                  <Typography.Text>{r.originType}</Typography.Text>
                </div>
                <div>
                  <Typography.Text type="secondary">
                    Expires At:{" "}
                  </Typography.Text>
                  <Typography.Text>
                    {r.expiresAt
                      ? formatLocalDate(new Date(r.expiresAt), true)
                      : "-"}
                  </Typography.Text>
                </div>
                <div>
                  <Typography.Text type="secondary">
                    Created At:{" "}
                  </Typography.Text>
                  <Typography.Text>
                    {r.createdAt
                      ? formatLocalDate(new Date(r.createdAt), true)
                      : "-"}
                  </Typography.Text>
                </div>
                <div>
                  <Typography.Text type="secondary">
                    Updated At:{" "}
                  </Typography.Text>
                  <Typography.Text>
                    {r.updatedAt
                      ? formatLocalDate(new Date(r.updatedAt), true)
                      : "-"}
                  </Typography.Text>
                </div>
              </Space>
            </div>
          ),
        }}
      />
    </div>
  );
}
